import { Button, Link as ChakraLink, Flex, Heading, Image, Progress, Text } from "@chakra-ui/react";
import { ReactNode } from "react";
import { Link as WouterLink } from "wouter";

import { useRestartProject } from "@/queries/mutate-project";

import { ReportSelf } from "../features/report/components/report-self";
import { projectIsFailed, projectIsLoading, useProjectQuery } from "../queries/query-projects";
import { ProjectProvider } from "../stores/project-provider";
interface ReportProps {
  slug: string;
}

const ReportNonSuccessWrapper = ({ children }: { children: ReactNode }) => {
  const LOGO_HEIGHT = "72px";
  return (
    <Flex p={4} w="100vw" h="100vh" bg="brand.page_bg" alignItems="center" justifyContent="center">
      <Flex
        my={LOGO_HEIGHT}
        flexDirection="column"
        gap={4}
        minW="320px"
        maxW="672px"
        bg="white"
        position="relative"
      >
        <Image
          position="absolute"
          bottom="100%"
          left={0}
          alt="klimaatscan logo"
          h={LOGO_HEIGHT}
          mb={4}
          src="/assets/images/klimaatscan-logo.png"
        />
        {children}
      </Flex>
    </Flex>
  );
};

export function ReportPage({ slug }: ReportProps) {
  const projectResponse = useProjectQuery(slug);

  const { mutate: restartProject, isPending } = useRestartProject();
  const project = projectResponse.data;

  if (projectResponse.isLoading) return null;

  if (!project)
    return (
      <ReportNonSuccessWrapper>
        <Heading as="h2" size="h2">
          Geen berekening met deze <br /> slug gevonden
        </Heading>
        <Text>
          Wilt u een nieuwe berekening starten? Klik dan{" "}
          <ChakraLink to={`/`} as={WouterLink} textDecoration="underline">
            hier
          </ChakraLink>
          .
        </Text>
      </ReportNonSuccessWrapper>
    );

  if (projectIsLoading(project)) {
    const maps = project.themes.flatMap((theme) => theme.maps || []);
    const loadedMaps = maps.filter((map) => map.calculated);
    return (
      <ReportNonSuccessWrapper>
        <Heading as="h2" size="h2">
          De berekening is gestart
        </Heading>
        <Text>Zodra deze is voltooid, verschijnt de rapportage op deze pagina.</Text>
        <Text>
          Wilt u een nieuwe berekening starten? Klik dan{" "}
          <ChakraLink to={`/`} as={WouterLink} textDecoration="underline">
            hier
          </ChakraLink>
          .
        </Text>
        <Flex alignItems="center" gap={2}>
          <Progress
            sx={{
              div: {
                bg: "brand.primary ",
              },
            }}
            w="400px"
            isAnimated
            value={loadedMaps.length}
            max={maps.length}
          />
          {` ${loadedMaps.length} / ${maps.length} kaarten geladen`}
        </Flex>

        <Text>
          U hoeft de pagina niet handmatig te vernieuwen; de berekening wordt automatisch
          bijgewerkt.
        </Text>
      </ReportNonSuccessWrapper>
    );
  }

  if (projectIsFailed(project)) {
    const { slug } = project;
    if (!slug) {
      throw new Error("This will never happen");
    }
    return (
      <ReportNonSuccessWrapper>
        <Heading as="h2" size="h2">
          Er is een fout opgetreden
        </Heading>
        <Text>
          Probeer de berekening opnieuw te starten met de knop hieronder. Als het probleem zich
          blijft voordoen, neem dan contact op met de beheerder van de Klimaatscan.
        </Text>
        <Button
          w="fit-content"
          variant="primary"
          onClick={() => restartProject(slug)}
          isLoading={isPending}
        >
          Herstart berekening
        </Button>
      </ReportNonSuccessWrapper>
    );
  }

  return (
    <ProjectProvider project={project}>
      <ReportSelf />
    </ProjectProvider>
  );
}
